import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, EMPTY } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { ChartConfiguration, ChartOptions } from 'chart.js';

import { DashboardWidgetsModule } from 'app/inter-chain/dashboard/widgets/dashboard-widgets.module';
import { SemanticService } from 'app/inter-chain/semantic/semantic.service';
import { SemanticAverageValues, SemanticDataResponse } from 'app/dashboard/models/WidgetDashboard.model.component';
import { SemanticTrendsData } from 'app/inter-chain/semantic/models/SemanticTrendsData.model';
import { DashboardWidgetCardComponent } from '../dashboard-widget-card/dashboard-widget-card.component';
import {
  buildChartDataset,
  buildPercentChartOptions,
  emptyChartData,
  fillTimeSeriesGaps,
  padDateRange,
  DECREASE_COLOR,
  DECREASE_BG_COLOR,
  INCREASE_COLOR,
  INCREASE_BG_COLOR,
} from '../../utils/chart-config.util';
import { getIROAndSemanticQualificationKey } from '../../utils/qualifications.util';

@Component({
  selector: 'mh-semantic-index-widget-container',
  templateUrl: './semantic-index-widget-container.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DashboardWidgetsModule, DashboardWidgetCardComponent, TranslateModule],
})
export class SemanticIndexWidgetContainerComponent {
  customerId = input.required<number>();
  enabled = input(true);

  rawData = signal<SemanticAverageValues | null>(null);
  displayValue = signal<string | null>(null);
  loading = signal(true);
  error = signal(false);
  ready = computed(() => !this.loading());
  chartData = signal<ChartConfiguration['data']>(emptyChartData());
  chartOptions = signal<ChartOptions>({});

  changePercentage = computed(() => this.rawData()?.sentiment_index?.variation ?? null);
  isIncrease = computed(() => (this.rawData()?.sentiment_index?.variation ?? 0) > 0);

  private destroyRef = inject(DestroyRef);
  private semanticService = inject(SemanticService);
  private translateService = inject(TranslateService);

  constructor() {
    combineLatest([toObservable(this.customerId), toObservable(this.enabled)])
      .pipe(
        switchMap(([, enabled]) => {
          this.reset();
          if (!enabled) {
            this.setDisabled();
            return EMPTY;
          }
          return this.fetchData();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private reset(): void {
    this.rawData.set(null);
    this.displayValue.set(null);
    this.loading.set(true);
    this.error.set(false);
    this.chartData.set(emptyChartData());
    this.chartOptions.set({});
  }

  private setDisabled(): void {
    this.loading.set(false);
  }

  private fetchData() {
    const today = DateTime.now().toFormat('yyyy-MM-dd');
    const monthAgo = DateTime.now().minus({ days: 30 }).toFormat('yyyy-MM-dd');
    const dates = { startDate: monthAgo, endDate: today };

    return this.semanticService.getSentimentIndex(dates, `${this.customerId()}`).pipe(
      tap((response: SemanticDataResponse) => {
        if (!response) return;
        const avg = response.average;
        avg.description = this.translateService.instant(
          getIROAndSemanticQualificationKey(avg?.sentiment_index?.actual),
        );
        this.rawData.set(avg);
        this.displayValue.set(avg.sentiment_index?.actual != null ? `${avg.sentiment_index.actual.toFixed()}%` : null);
      }),
      switchMap((response: SemanticDataResponse) => {
        if (!response) return EMPTY;
        return this.semanticService.getSentimentIndexTrends(dates, `${this.customerId()}`, 'days').pipe(
          tap((trendsResponse: SemanticTrendsData[]) => {
            const rawPeriods = trendsResponse.map((item) => ({
              period: item.period,
              value: item.data.semantic_index ?? null,
            }));
            const padded = padDateRange(rawPeriods, monthAgo, today);
            const filled = fillTimeSeriesGaps(padded);
            const semanticValues = filled.map((p) => p.value as number);
            const nonNullValues = filled.map((p) => p.value).filter((v): v is number => v !== null);
            const increase = this.isIncrease();
            this.chartOptions.set(buildPercentChartOptions(nonNullValues.length ? Math.max(...nonNullValues) : 100));
            this.chartData.set(
              buildChartDataset(
                filled.map((p) => p.period),
                semanticValues,
                increase ? INCREASE_COLOR : DECREASE_COLOR,
                increase ? INCREASE_BG_COLOR : DECREASE_BG_COLOR,
              ),
            );
          }),
          catchError(() => EMPTY),
        );
      }),
      catchError(() => {
        this.error.set(true);
        return EMPTY;
      }),
      finalize(() => this.loading.set(false)),
    );
  }
}
